import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TrendingUp, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common';
import ExamList from './ExamList';
import StudentManagement from './StudentManagement';
import ReportModal from '../components/ReportModal';
// 데스크톱 확장 추가
import { useDesktopExpanded } from '../hooks/useDesktopExpanded';
import '../styles/desktop-expanded.css';

function Dashboard() {
  const { getAccessToken, userType } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [statsData, setStatsData] = useState(null);

  // 차트 데이터 state
  const [subjectTestData, setSubjectTestData] = useState([]);
  const [gradeExamData, setGradeExamData] = useState([]);
  const [teacherTestData, setTeacherTestData] = useState([]);
  const [gradeStudentData, setGradeStudentData] = useState([]);

  // 데스크톱 확장 훅 사용
  const {
    isMobileView,
    getComponentClasses
  } = useDesktopExpanded({
    expandConfig: {
      mode: 'grid',
      maintainMobileWidth: true
    }
  });
  
  // 카드 데이터 상태 추가 (추가 카드를 위한 준비)
  const [cardItems, setCardItems] = useState([]);
  
  // 최신 리포트 발행 결과 상태 추가
  const [recentReports, setRecentReports] = useState([]);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [isReportLoading, setIsReportLoading] = useState(false);
  
  // 문제 현황 상태 추가 (비활성화 - dashboard API 사용)
  // const [problemsData, setProblemsData] = useState({
  //   todayCount: 0,
  //   totalCount: 0
  // });
  // const [isProblemsLoading, setIsProblemsLoading] = useState(false);
  
  // 리포트 모달 상태
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
      }
    };
    checkAuth();
  }, [getAccessToken, navigate]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('대시보드 데이터를 불러오는데 실패했습니다.');
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (error) {
        console.error('대시보드 데이터 조회 오류:', error);
      }
    };

    fetchDashboardData();
  }, [getAccessToken]);

  // 통계 데이터 (재원생 수 계산용) 조회
  useEffect(() => {
    const fetchStatsData = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/stats`, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setStatsData(data.data);

          // 차트 데이터 처리
          const exam_fields = data.data?.exam_fields;

          // 과목별 테스트/리포트 현황 차트 데이터
          if (exam_fields && (exam_fields.exams_by_tree || exam_fields.reports_by_tree)) {
            const allTrees = new Set();
            if (exam_fields.exams_by_tree) {
              Object.keys(exam_fields.exams_by_tree).forEach(tree => allTrees.add(tree));
            }
            if (exam_fields.reports_by_tree) {
              Object.keys(exam_fields.reports_by_tree).forEach(tree => allTrees.add(tree));
            }

            const chartData = Array.from(allTrees).map(subject => ({
              subject,
              testCount: exam_fields.exams_by_tree?.[subject] || 0,
              reportCount: exam_fields.reports_by_tree?.[subject] || 0
            })).sort((a, b) => (b.testCount + b.reportCount) - (a.testCount + a.reportCount));

            setSubjectTestData(chartData);
          }

          // 강사별 테스트/리포트 현황 차트 데이터
          if (exam_fields && (exam_fields.exams_by_teacher || exam_fields.reports_by_teacher)) {
            const allTeachers = new Set();
            if (exam_fields.exams_by_teacher) {
              Object.keys(exam_fields.exams_by_teacher).forEach(teacher => allTeachers.add(teacher));
            }
            if (exam_fields.reports_by_teacher) {
              Object.keys(exam_fields.reports_by_teacher).forEach(teacher => allTeachers.add(teacher));
            }

            const chartData = Array.from(allTeachers).map(teacher => ({
              teacher,
              testCount: exam_fields.exams_by_teacher?.[teacher] || 0,
              reportCount: exam_fields.reports_by_teacher?.[teacher] || 0
            })).sort((a, b) => (b.testCount + b.reportCount) - (a.testCount + a.reportCount))
              .slice(0, 8);

            setTeacherTestData(chartData);
          }

          // 학년별 테스트/리포트 현황 차트 데이터
          if (exam_fields && (exam_fields.exams_by_grade || exam_fields.reports_by_grade)) {
            const gradeMapping = {
              7: '중1', 8: '중2', 9: '중3',
              10: '고1', 11: '고2', 12: '고3'
            };

            const allGrades = new Set();
            if (exam_fields.exams_by_grade) {
              Object.keys(exam_fields.exams_by_grade).forEach(grade => {
                const gradeNum = parseInt(grade);
                if (gradeMapping[gradeNum]) allGrades.add(gradeNum);
              });
            }
            if (exam_fields.reports_by_grade) {
              Object.keys(exam_fields.reports_by_grade).forEach(grade => {
                const gradeNum = parseInt(grade);
                if (gradeMapping[gradeNum]) allGrades.add(gradeNum);
              });
            }

            const chartData = Array.from(allGrades).map(gradeNum => {
              const testCount = exam_fields.exams_by_grade?.[gradeNum] || 0;
              const reportCount = exam_fields.reports_by_grade?.[gradeNum] || 0;
              return {
                grade: gradeMapping[gradeNum],
                testCount,
                reportCount
              };
            }).filter(item => item.testCount > 0 || item.reportCount > 0)
              .sort((a, b) => {
                const gradeOrder = ['중1', '중2', '중3', '고1', '고2', '고3'];
                return gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade);
              });

            setGradeExamData(chartData);
          }

          // 학년별 학생 분포 차트 데이터 (특별 강사 학생 제외)
          const student_fields = data.data?.student_fields;
          if (student_fields?.students_by_grade) {
            const specialTeacherNames = ['데스크', 'unassigned', '테스트', '문제입력', '데스크2'];

            // 특별 강사의 학년별 합계 계산
            let specialByGrade = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 };
            if (student_fields.students_by_teacher) {
              Object.entries(student_fields.students_by_teacher).forEach(([teacherName, grades]) => {
                if (specialTeacherNames.includes(teacherName)) {
                  for (let i = 1; i <= 12; i++) {
                    specialByGrade[i] += grades[i] || 0;
                  }
                }
              });
            }

            const studentChartData = [];

            // 초등 (배열 인덱스 1~6 합계)
            const elemTotal = (student_fields.students_by_grade[1] || 0) + (student_fields.students_by_grade[2] || 0) +
                              (student_fields.students_by_grade[3] || 0) + (student_fields.students_by_grade[4] || 0) +
                              (student_fields.students_by_grade[5] || 0) + (student_fields.students_by_grade[6] || 0);
            const specialElemTotal = specialByGrade[1] + specialByGrade[2] + specialByGrade[3] +
                                     specialByGrade[4] + specialByGrade[5] + specialByGrade[6];
            studentChartData.push({ grade: '초등', count: elemTotal - specialElemTotal });

            // 중1~중3
            studentChartData.push(
              { grade: '중1', count: (student_fields.students_by_grade[7] || 0) - specialByGrade[7] },
              { grade: '중2', count: (student_fields.students_by_grade[8] || 0) - specialByGrade[8] },
              { grade: '중3', count: (student_fields.students_by_grade[9] || 0) - specialByGrade[9] }
            );

            // 고1~고3
            studentChartData.push(
              { grade: '고1', count: (student_fields.students_by_grade[10] || 0) - specialByGrade[10] },
              { grade: '고2', count: (student_fields.students_by_grade[11] || 0) - specialByGrade[11] },
              { grade: '고3', count: (student_fields.students_by_grade[12] || 0) - specialByGrade[12] }
            );

            setGradeStudentData(studentChartData);
          }
        }
      } catch (error) {
        console.error('통계 데이터 조회 오류:', error);
      }
    };

    fetchStatsData();
  }, [getAccessToken]);

  // 최신 리포트 데이터 가져오기 함수
  const fetchRecentReports = async () => {
    try {
      setIsReportLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // api/reports 엔드포인트를 사용하여 최신 리포트 10개 가져오기
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/reports?page=1&per_page=10`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`API 호출 실패: ${response.status}`);
      }

      const reportsData = await response.json();
      console.log('API Reports Response:', reportsData);
      
      // 전체 개수 설정
      setTotalReportsCount(reportsData.total_items || 0);
      
      // API 응답 데이터를 처리
      let dataArray = [];
      if (reportsData && Array.isArray(reportsData.data)) {
        dataArray = reportsData.data;
      } else if (Array.isArray(reportsData)) {
        dataArray = reportsData;
      } else {
        console.warn('Unexpected API response format:', reportsData);
        dataArray = [];
      }
      
      // 리포트 데이터를 테이블에 맞는 형식으로 변환
      const reportsWithStatus = dataArray.map(report => {
        console.log('Individual report data:', report); // 디버깅용
        return {
          exam_id: report.exam_id || report.test_id || '-',
          stamp_id: report.stamp_id || report.student_info?.stamp_id || report.exam_info?.stamp_id || '-',
          student_id: report.student_info?.student_seq || report.student_id || '-',
          student_name: report.student_info?.student_name || '알 수 없음',
          school_name: report.student_info?.school_name || '학교명 없음',
          school_grade: report.student_info?.school_grade || '-',
          tree_name: report.tree_info?.tree_name || '과목명 없음',
          report_status: report.is_closed === true ? 999 : 200,
          created_at: report.created_at || new Date().toISOString(),
          report_created_at: report.created_at || new Date().toISOString(),
          report_updated_at: report.updated_at || null,
          // 추가 정보
          rating: report.rating || null,
          difficulty: report.difficulty || null,
          exam_type: report.exam_type || null
        };
      });
      
      console.log('Processed Reports:', reportsWithStatus);
      setRecentReports(reportsWithStatus);
      
    } catch (error) {
      console.error('Error fetching recent reports:', error);
      
      // API 호출 실패 시 빈 배열로 설정
      setRecentReports([]);
      
      // 사용자에게 에러 알림 (선택사항)
      // alert('최신 리포트 데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsReportLoading(false);
    }
  };

  // 문제 현황 데이터 가져오기 함수 (비활성화 - dashboard API 사용)
  // const fetchProblemsData = async () => {
  //   try {
  //     setIsProblemsLoading(true);
  //     const token = await getAccessToken();
  //     if (!token) {
  //       navigate('/signin');
  //       return;
  //     }

  //     // 문제 목록 API 호출 (큰 페이지 사이즈로 전체 데이터 가져오기)
  //     const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/problems/?page=1&per_page=1000`;
      
  //     const response = await fetch(apiUrl, {
  //       headers: {
  //         'Authorization': token,
  //         'Content-Type': 'application/json',
  //       }
  //     });

  //     if (!response.ok) {
  //       throw new Error(`API 호출 실패: ${response.status}`);
  //     }

  //     const problemsData = await response.json();
  //     console.log('API Problems Response:', problemsData);
      
  //     if (problemsData.status === 'success') {
  //       const totalCount = problemsData.total_items || problemsData.total_count || problemsData.data.length;
        
  //       // 오늘 생성된 문제 개수 계산
  //       const today = new Date();
  //       const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  //       const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
        
  //       const todayCount = problemsData.data.filter(problem => {
  //         const createdDate = new Date(problem.created_at);
  //         return createdDate >= todayStart && createdDate < todayEnd;
  //       }).length;
        
  //       setProblemsData({
  //         todayCount,
  //         totalCount
  //       });
  //     } else {
  //       console.warn('Problems API response not successful:', problemsData);
  //       setProblemsData({
  //         todayCount: 0,
  //         totalCount: 0
  //       });
  //     }
      
  //   } catch (error) {
  //     console.error('Error fetching problems data:', error);
      
  //     // API 호출 실패 시 0으로 설정
  //     setProblemsData({
  //       todayCount: 0,
  //       totalCount: 0
  //     });
  //   } finally {
  //     setIsProblemsLoading(false);
  //   }
  // };

  // 최신 리포트 데이터 가져오기
  useEffect(() => {
    fetchRecentReports();
  }, []);

  // 문제 현황 데이터 가져오기 (비활성화 - dashboard API 사용)
  // useEffect(() => {
  //   fetchProblemsData();
  // }, []);

  const CompactStatCard = ({ title, mainValue, mainLabel, subValue, subLabel, onClick, icon, showDelta = true, className = '' }) => (
    <div className={`rounded-3xl bg-white p-4 shadow-md sm:p-6 ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''} ${className}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="mb-1 text-base font-bold text-gray-600 sm:mb-2 sm:text-lg">{title}</h4>
          <div className="flex flex-col text-right">
            <div>
              <p className={`flex items-center justify-end text-right text-4xl font-bold sm:text-5xl ${
                (mainValue === 0 || mainValue === '0' || mainValue === '+0') ? 'text-gray-400' : 'text-primary'
              }`}>
                {typeof mainValue === 'string' && mainValue.startsWith('+') ? (
                  <>
                    <span className="text-2xl sm:text-3xl">+</span>
                    {mainValue.slice(1)}
                  </>
                ) : (
                  mainValue
                )}
              </p>
              <p className="mt-0.5 text-right text-xs text-gray-500 sm:mt-1">{mainLabel}</p>
            </div>
            {subValue !== undefined && (
              <div className="mt-1 text-right text-xs">
                {showDelta ? (
                  <span className={`font-medium ${subValue > 0 ? 'text-green-600' : subValue < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    {subValue > 0 ? '+' : ''}{subValue}
                  </span>
                ) : (
                  <span 
                    className="text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: typeof subValue === 'string' 
                        ? subValue.replace(/(\d+)/g, '<strong>$1</strong>')
                        : subValue
                    }}
                  />
                )}
                {subLabel && <span className="ml-1 text-xs text-gray-500">{subLabel}</span>}
              </div>
            )}
          </div>
        </div>
        {icon && (
          <div className="ml-2 text-gray-400 sm:ml-4">
            {icon}
          </div>
        )}
      </div>
    </div>
  );

  const StatCard = ({ title, stats, onClick, clickableStats }) => (
    <div className={`rounded-3xl bg-white p-6 shadow-md ${onClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
      onClick={onClick}
    >
      <h4 className="mb-4 text-lg font-semibold text-gray-800">{title}</h4>
      <div className="space-y-3">
        {Object.entries(stats).map(([key, value]) => {
          const isClickable = clickableStats && clickableStats[key];
          return (
            <div 
              key={key} 
              className={`flex items-center justify-between ${isClickable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
              onClick={(e) => {
                if (isClickable) {
                  e.stopPropagation();
                  isClickable();
                }
              }}
            >
              <span className={`text-gray-600 ${isClickable ? 'hover:text-blue-600' : ''}`}>{key}</span>
              <span className="font-semibold text-gray-800">{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const handleStudentCardClick = () => {
    navigate('/student/1');
  };

  const handleReservationClick = () => {
    navigate('/reservations');
  };

  const handleExamListClick = () => {
    navigate('/student/exam-list');
  };

  const handleReportClick = async (report) => {
    console.log('Report clicked:', report);
    console.log('Exam data for modal:', {
      stamp_id: report.stamp_id,
      exam_id: report.exam_id,
      student_id: report.student_id
    });

    // stamp_id가 없거나 '-'인 경우 학생별 stamp_id 조회
    if (!report.stamp_id || report.stamp_id === '-') {
      try {
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        // 학생별 stamp 목록 조회
        const stampResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${report.student_id}`,
          {
            headers: {
              'Authorization': token,
              'Content-Type': 'application/json',
            }
          }
        );

        if (!stampResponse.ok) {
          console.error('Stamp API response error:', stampResponse.status, stampResponse.statusText);
          if (stampResponse.status === 401) {
            navigate('/signin');
            return;
          }
          alert('스탬프 정보를 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.');
          return;
        }

        const stampData = await stampResponse.json();
        
        // stamps 데이터 구조 검증
        if (!stampData || !stampData.data) {
          console.error('Invalid stamp data structure:', stampData);
          alert('스탬프 데이터 형식이 올바르지 않습니다.');
          return;
        }
        
        const stamps = stampData.data.stamps || [];
        
        if (stamps.length === 0) {
          console.warn('No stamps found for student:', report.student_id);
          alert('해당 학생의 스탬프 정보가 없습니다.');
          return;
        }
        
        // exam_id와 매치되는 stamp를 찾기 위해 각 stamp의 exam 목록 확인
        for (const stamp of stamps) {
          if (!stamp.stamp_id) {
            console.warn('Invalid stamp without stamp_id:', stamp);
            continue;
          }
          
          try {
            const examResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp.stamp_id}`,
              {
                headers: {
                  'Authorization': token,
                  'Content-Type': 'application/json',
                }
              }
            );

            if (!examResponse.ok) {
              console.warn(`Failed to fetch exams for stamp ${stamp.stamp_id}:`, examResponse.status);
              continue;
            }

            const examData = await examResponse.json();
            const exams = examData.data || [];
            
            // 해당 exam_id가 이 stamp에 속하는지 확인
            const matchingExam = exams.find(exam => exam.exam_id === report.exam_id);
            if (matchingExam) {
              // 매치되는 stamp_id를 찾았으므로 리포트 정보 업데이트
              const updatedReport = {
                ...report,
                stamp_id: stamp.stamp_id
              };
              console.log('Updated report with stamp_id:', updatedReport);
              setSelectedReport(updatedReport);
              setIsReportModalOpen(true);
              return;
            }
          } catch (examError) {
            console.error(`Error fetching exams for stamp ${stamp.stamp_id}:`, examError);
            continue;
          }
        }
        
        console.warn('stamp_id를 찾을 수 없습니다. exam_id:', report.exam_id);
        alert('해당 시험에 대한 스탬프를 찾을 수 없습니다.');
        return;
      } catch (error) {
        console.error('stamp_id 조회 중 오류:', error);
        alert('stamp_id 조회 중 오류가 발생했습니다. 네트워크 연결을 확인해주세요.');
        return;
      }
    }

    // stamp_id가 있는 경우 바로 모달 열기
    setSelectedReport(report);
    setIsReportModalOpen(true);
  };

  // const TreeCard = ({ title, trees }) => (
  //   <div className="bg-white rounded-3xl shadow-md p-6">
  //     <h4 className="text-lg font-semibold mb-4 text-gray-800">{title}</h4>
  //     <div className="overflow-x-auto">
  //       <table className="min-w-full table-auto">
  //         <thead>
  //           <tr className="bg-gray-50">
  //             <th className="px-4 py-2 text-left">과목</th>
  //             <th className="px-4 py-2 text-left">교육과정</th>
  //             {title === "Closed Tree" && (
  //               <>
  //                 <th className="px-4 py-2 text-left">필드 수</th>
  //                 <th className="px-4 py-2 text-left">출제 가능 문제 수</th>
  //               </>
  //             )}
  //           </tr>
  //         </thead>
  //         <tbody>
  //           {trees.map((tree, index) => (
  //             <tr key={tree.tree_id} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
  //               <td className="px-4 py-2">{tree.tree_name}</td>
  //               <td className="px-4 py-2">{tree.edu_ver}</td>
  //               {title === "Closed Tree" && (
  //                 <>
  //                   <td className="px-4 py-2">{tree.prob_slots}</td>
  //                   <td className="px-4 py-2">{tree.probs_num}</td>
  //                 </>
  //               )}
  //             </tr>
  //           ))}
  //         </tbody>
  //       </table>
  //     </div>
  //   </div>
  // );

  return (
    <Layout>
      <div className="h-full">
        {dashboardData ? (
          <div className="pb-4">
            <div className={isMobileView ? "grid grid-cols-2 gap-2 sm:gap-4 md:gap-6" : "grid grid-cols-4 gap-4 w-full"}>
              <CompactStatCard 
                title="Ky-Test" 
                mainValue={dashboardData.data.basic_stats.today_exams > 0 ? `+${dashboardData.data.basic_stats.today_exams}` : dashboardData.data.basic_stats.today_exams}
                // mainLabel="오늘 Ky-Test"
                subValue={`총 ${dashboardData.data.basic_stats.total_exams} 개`}
                // subLabel="개"
                onClick={handleExamListClick}
                showDelta={false}
              />
              <CompactStatCard
                title="신규 문제" 
                mainValue={dashboardData.data.basic_stats.today_problems > 0 ? `+${dashboardData.data.basic_stats.today_problems}` : dashboardData.data.basic_stats.today_problems}
                // mainLabel="오늘 생성된 문제"
                subValue={`총 ${dashboardData.data.basic_stats.total_problems} 개`}
                subLabel=""
                // 문제관리 v1 메뉴를 사이드바에서 가려 문제 v2 로 보낸다(2026-10-21)
                onClick={() => navigate('/problem-hub')}
                showDelta={false}
              />
              <CompactStatCard 
                title="Ky-Report" 
                mainValue={dashboardData.data.basic_stats.today_reports > 0 ? `+${dashboardData.data.basic_stats.today_reports}` : dashboardData.data.basic_stats.today_reports}
                // mainLabel="전체 리포트"
                subValue={`전체 ${dashboardData.data.basic_stats.total_reports} 개`}
                // subLabel="확정됨"
                onClick={() => {}}
                showDelta={false}
              />
              <CompactStatCard
                title="편성 문제"
                mainValue={statsData?.problem_fields?.assigned_problems || 0}
                subValue={`총 ${dashboardData.data.basic_stats.total_problems} 개`}
                subLabel=""
                className="cursor-pointer hover:bg-gray-50"
                showDelta={false}
              />
            </div>

            {/* 과목별 테스트/리포트 현황 - 한 행 전체 차지 */}
            <div className="mt-4">
              <div className="rounded-2xl bg-white p-4 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">과목별 현황(30일)</h4>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#10B981' }}></span>
                      <span className="text-gray-500">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#3B82F6' }}></span>
                      <span className="text-gray-500">리포트</span>
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={subjectTestData} margin={{ top: 15, right: 5, left: 0, bottom: 40 }} barGap={2} barCategoryGap="15%">
                    <defs>
                      <linearGradient id="colorSubjectDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                      </linearGradient>
                      <linearGradient id="colorSubjectReportDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#1D4ED8" stopOpacity={0.7}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="subject" tick={{ fontSize: 9, fill: '#9ca3af' }} stroke="#f3f4f6" tickLine={false} angle={-45} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} stroke="#f3f4f6" axisLine={false} tickLine={false} width={30} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.98)', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '11px' }} />
                    <Bar dataKey="testCount" fill="url(#colorSubjectDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="테스트">
                      <LabelList dataKey="testCount" position="top" style={{ fontSize: 9, fill: '#059669' }} />
                    </Bar>
                    <Bar dataKey="reportCount" fill="url(#colorSubjectReportDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="리포트">
                      <LabelList dataKey="reportCount" position="top" style={{ fontSize: 9, fill: '#1D4ED8' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 테스트/리포트 현황 차트 섹션 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {/* 학년별 테스트/리포트 현황 */}
              <div className="rounded-2xl bg-white p-4 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">학년별 현황(30일)</h4>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#8B5CF6' }}></span>
                      <span className="text-gray-500">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#EF4444' }}></span>
                      <span className="text-gray-500">리포트</span>
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={gradeExamData} margin={{ top: 15, right: 5, left: 0, bottom: 5 }} barGap={2} barCategoryGap="15%">
                    <defs>
                      <linearGradient id="colorGradeTestDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#7C3AED" stopOpacity={0.7}/>
                      </linearGradient>
                      <linearGradient id="colorGradeReportDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#DC2626" stopOpacity={0.7}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="grade" tick={{ fontSize: 10, fill: '#9ca3af' }} stroke="#f3f4f6" tickLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} stroke="#f3f4f6" axisLine={false} tickLine={false} width={30} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.98)', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '11px' }} />
                    <Bar dataKey="testCount" fill="url(#colorGradeTestDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="테스트">
                      <LabelList dataKey="testCount" position="top" style={{ fontSize: 9, fill: '#7C3AED' }} />
                    </Bar>
                    <Bar dataKey="reportCount" fill="url(#colorGradeReportDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="리포트">
                      <LabelList dataKey="reportCount" position="top" style={{ fontSize: 9, fill: '#DC2626' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* 강사별 테스트/리포트 현황 */}
              <div className="rounded-2xl bg-white p-4 shadow-md">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">강사별 현황(30일)</h4>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#F59E0B' }}></span>
                      <span className="text-gray-500">테스트</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded" style={{ background: '#10B981' }}></span>
                      <span className="text-gray-500">리포트</span>
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={teacherTestData} margin={{ top: 15, right: 5, left: 0, bottom: 40 }} barGap={2} barCategoryGap="15%">
                    <defs>
                      <linearGradient id="colorTeacherDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#D97706" stopOpacity={0.7}/>
                      </linearGradient>
                      <linearGradient id="colorTeacherReportDash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.7}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="teacher" tick={{ fontSize: 9, fill: '#9ca3af' }} stroke="#f3f4f6" tickLine={false} angle={-45} textAnchor="end" interval={0} />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} stroke="#f3f4f6" axisLine={false} tickLine={false} width={30} />
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.98)', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '11px' }} />
                    <Bar dataKey="testCount" fill="url(#colorTeacherDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="테스트">
                      <LabelList dataKey="testCount" position="top" style={{ fontSize: 9, fill: '#D97706' }} />
                    </Bar>
                    <Bar dataKey="reportCount" fill="url(#colorTeacherReportDash)" radius={[4, 4, 0, 0]} maxBarSize={30} name="리포트">
                      <LabelList dataKey="reportCount" position="top" style={{ fontSize: 9, fill: '#059669' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 최신 리포트 발행 결과 섹션 */}
            <div
              className="mt-4"
              style={isMobileView ? { paddingBottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' } : {}}
            >
              <h3 className="mb-3 text-2xl font-medium text-gray-700 sm:mb-4">최신 리포트</h3>
              <div className="rounded-3xl bg-white p-1.5 shadow-md sm:p-3 overflow-hidden">
                {isReportLoading ? (
                  <div className="py-4 text-center">로딩 중...</div>
                ) : recentReports.length > 0 ? (
                  <div className="relative">
                    <div className="w-full rounded-2xl">
                      <table className="w-full table-fixed text-xs sm:text-sm">
                        <tbody>
                          {(isMobileView ? recentReports.slice(0, 10) : recentReports).map((report, index) => {
                            const createdDate = new Date(report.report_created_at || report.created_at);
                            const updatedDate = new Date(report.report_updated_at || report.created_at);
                            
                            const formatDateTime = (date) => {
                              const year = String(date.getFullYear()).slice(2);
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const time = date.toLocaleTimeString('ko-KR', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              });
                              return `${year}.${month}.${day} ${time}`;
                            };
                            
                            return (
                              <tr key={report.exam_id} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="px-1 py-2 text-[10px] sm:px-2 sm:py-3 sm:text-sm" style={{width: '35%'}}>
                                   <div className="flex items-center gap-2 overflow-hidden">
                                     {/* 학생 ID 배지 */}
                                     <div 
                                       className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                                       style={{ width: '32px', height: '32px' }}
                                       title={`학생 ID: ${report.student_id}`}
                                     >
                                       {report.student_id}
                                     </div>
                                     
                                     {/* 학생 정보 */}
                                     <div className="min-w-0 overflow-hidden">
                                       <div className="truncate text-sm font-medium sm:text-lg">{report.student_name}</div>
                                       <div className="truncate text-[9px] text-gray-500 sm:text-xs">
                                         {report.school_name} {report.school_grade}학년
                                       </div>
                                     </div>
                                   </div>
                                 </td>
                                <td className="px-1 py-2 text-[10px] sm:px-2 sm:py-3 sm:text-sm" style={{width: '23'}}>
                                  <div className="truncate">
                                    {report.tree_name}
                                  </div>
                                </td>
                                <td className="px-1 py-2 text-[9px] sm:px-2 sm:py-3 sm:text-xs" style={{width: '22%'}}>
                                  <div className="truncate">
                                    {report.report_updated_at ? formatDateTime(updatedDate) : formatDateTime(createdDate)}
                                  </div>
                                </td>
                                <td className="px-1 py-2 sm:px-2 sm:py-3" style={{width: '10%'}}>
                                  <button
                                    className={`flex size-8 cursor-pointer items-center justify-center rounded-3xl border border-gray-300 bg-transparent p-0 hover:opacity-80 ${
                                      report.report_status === 999 ? 'text-primary hover:text-primary-hover' :
                                      'text-blue-500 hover:text-blue-600'
                                    }`}
                                    onClick={() => handleReportClick(report)}
                                    title={report.report_status === 999 ? '확정된 리포트' : '미확정 리포트'}
                                  >
                                    <FileText size={16} strokeWidth={2.5} />
                                  </button>
                                </td>
                                <td className="px-1 py-2 sm:px-2 sm:py-3" style={{width: '10%'}}>
                                  <Button
                                    variant="ghost"
                                    size="xs"
                                    className="flex size-8 items-center justify-center rounded-3xl p-0 text-primary hover:text-primary-hover"
                                    onClick={() => navigate(`/learning-status/student/${report.student_id}/test/${report.exam_id}`)}
                                    title="학습분석"
                                  >
                                    <TrendingUp size={16} strokeWidth={2.5} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    최신 리포트가 없습니다.
                  </div>
                )}
              </div>
            </div>
            
            {/* 예약 관리 섹션 추가 - 주석 처리
            <div className="mt-6 sm:mt-10">
              <h3 className="mb-3 text-2xl font-medium text-gray-700 sm:mb-4">예약 현황</h3>
              <div className="rounded-3xl bg-white p-3 shadow-md sm:p-6">
                <div className="mb-4">
                  <div className="mb-3 flex items-center justify-between sm:mb-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => shiftDates('prev')}
                    >
                      &lt;
                    </Button>
                    <div className="text-base font-bold sm:text-xl">
                      {`${startDate.getFullYear()}년 ${startDate.getMonth() + 1}월`}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => shiftDates('next')}
                    >
                      &gt;
                    </Button>
                  </div>
                  <div className="w-full overflow-x-auto">
                    <div className="flex min-w-[400px] border-l border-gray-200">
                      {renderCalendarDays()}
                    </div>
                  </div>
                </div>

                {isLoading ? (
                  <div className="py-4 text-center">로딩 중...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="w-full px-3 sm:px-6">
                      <table className="w-full divide-y divide-gray-200 text-xs sm:text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">학생 ID</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">학생 이름</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">이메일</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">전화번호</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">학부모 전화번호</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">학교</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">학년</th>
                            <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">예약 시간</th>
                            {userType === 'manager' && (
                              <th className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">작업</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {currentReservations.length > 0 ? (
                            currentReservations.map((reservation) => (
                              <tr
                                key={reservation.student_id}
                                className="hover:bg-primary/10 cursor-pointer transition-colors duration-150"
                              >
                                <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap rounded-l-3xl">
                                  <div className="flex items-center">
                                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70">
                                      {reservation.student_id}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                                  <span className="font-medium text-gray-900">{reservation.student_name}</span>
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3">
                                  <div className="max-w-[150px] truncate sm:max-w-none" title={reservation.student_email}>
                                    {reservation.student_email}
                                  </div>
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">{reservation.mobile_number}</td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">{reservation.parent_mobile_number}</td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3">
                                  <div className="max-w-[100px] truncate sm:max-w-none" title={reservation.school_name}>
                                    {reservation.school_name}
                                  </div>
                                </td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 text-center">{reservation.school_grade}</td>
                                <td className="px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">
                                  {new Date(reservation.rsv_time * 1000).toLocaleTimeString('ko-KR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false
                                  })}
                                </td>
                                {userType === 'manager' && (
                                  <td className="px-3 py-2 sm:px-4 sm:py-3">
                                    <Button
                                      variant="ghost"
                                      size="xs"
                                      className="whitespace-nowrap text-danger hover:text-danger"
                                      onClick={() => navigate(`/student/${reservation.student_id}/stamp`)}
                                    >
                                      시험
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={userType === 'manager' ? 9 : 8} className="py-8 text-center text-gray-500">
                                선택한 날짜에 예약이 없습니다.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
            */}
          </div>
        ) : (
          <div className="mt-8 text-center">
            <p>데이터를 불러오는 중...</p>
          </div>
        )}
      </div>
      
      {/* 리포트 모달 */}
      {isReportModalOpen && selectedReport && (
        <ReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          examData={{
            stamp_id: selectedReport.stamp_id,
            exam_id: selectedReport.exam_id,
            student_id: selectedReport.student_id
          }}
        />
      )}
    </Layout>
  );
}

export default Dashboard;
